import {
  DayPlanEventSource,
  DayPlanScheduleDrawerMode,
  DERIVED_ROUTINE_STATUS,
  DurationDisplayKind,
  EventSeriesRRULEFreq,
  FileImportStatusMode,
  HABIT_COMPLETION_STATUS,
  ImportEventRepeat,
  RRULEByDay
} from 'enums';
import { ImportRepeatRRULEConfig } from 'interfaces/day-plan.interface';

/** Rows extracted below this confidence are flagged for the user to review. */
export const LOW_CONFIDENCE_THRESHOLD = 0.6;

/**
 * Index of the sole date bucket when import one-offs share one day
 * (`hasSingleItem(dateGroups)` → flat list, no date accordion).
 */
export const FILE_IMPORT_SINGLE_DAY_GROUP_INDEX = 0;

/** Separator between a `planned_block_key` prefix and its value. */
export const BLOCK_KEY_SEPARATOR = ':';

/** Milliseconds in one minute, for gap-duration maths. */
export const MS_PER_MINUTE = 60_000;

/** Milliseconds in one second — activities carry `duration_seconds`. */
export const MS_PER_SECOND = 1_000;

/**
 * Header emoji for the built-in and scheduled routine blocks on the timeline.
 * Morning/evening mirror the native app; custom routines share one recurring glyph.
 */
export const MORNING_ROUTINE_EMOJI = '🌅';
export const EVENING_ROUTINE_EMOJI = '🌙';
export const CUSTOM_ROUTINE_EMOJI = '🔁';

/** Stable timeline-block ids for the built-in routines (used as React keys). */
export const MORNING_ROUTINE_BLOCK_ID = 'morning';
export const EVENING_ROUTINE_BLOCK_ID = 'evening';

/** Prefix for a scheduled custom routine's block id (`custom-<routineId>`). */
export const CUSTOM_ROUTINE_BLOCK_ID_PREFIX = 'custom-';

/**
 * Day step for each import repeat kind when computing RRULE COUNT
 * (matches BE `IMPORT_REPEAT_INTERVAL_DAYS`).
 */
export const IMPORT_REPEAT_INTERVAL_DAYS = {
  daily: 1,
  weekly: 7,
  fortnightly: 14
} as const;

/**
 * Moment weekday index (`day()`: 0 = Sunday … 6 = Saturday) → RFC 5545 BYDAY.
 */
export const RRULE_BYDAY_BY_MOMENT_WEEKDAY: readonly RRULEByDay[] = [
  RRULEByDay.SU,
  RRULEByDay.MO,
  RRULEByDay.TU,
  RRULEByDay.WE,
  RRULEByDay.TH,
  RRULEByDay.FR,
  RRULEByDay.SA
];

/** Named RFC 5545 RRULE literals used when building import series rules. */
export const RRULE_PARTS = {
  PREFIX: 'RRULE:',
  KEY: {
    FREQ: 'FREQ',
    INTERVAL: 'INTERVAL',
    BYDAY: 'BYDAY',
    COUNT: 'COUNT'
  },
  FREQ: {
    DAILY: EventSeriesRRULEFreq.DAILY,
    WEEKLY: EventSeriesRRULEFreq.WEEKLY
  },
  INTERVAL: {
    FORTNIGHTLY_WEEKS: 2
  }
} as const;

/** i18n keys for RRULE BYDAY → weekday labels in series summaries. */
export const RRULE_BYDAY_LABEL_KEYS: Record<RRULEByDay, string> = {
  [RRULEByDay.MO]: 'day_plan.rrule_weekday_monday',
  [RRULEByDay.TU]: 'day_plan.rrule_weekday_tuesday',
  [RRULEByDay.WE]: 'day_plan.rrule_weekday_wednesday',
  [RRULEByDay.TH]: 'day_plan.rrule_weekday_thursday',
  [RRULEByDay.FR]: 'day_plan.rrule_weekday_friday',
  [RRULEByDay.SA]: 'day_plan.rrule_weekday_saturday',
  [RRULEByDay.SU]: 'day_plan.rrule_weekday_sunday'
};

/**
 * Per import frequency → RRULE shape. Adding a recurring `ImportEventRepeat`
 * value without updating this map is a compile error. `NONE` is excluded.
 */
export const IMPORT_REPEAT_RRULE_CONFIG: Record<
  Exclude<ImportEventRepeat, ImportEventRepeat.NONE>,
  ImportRepeatRRULEConfig
> = {
  [ImportEventRepeat.DAILY]: {
    freq: EventSeriesRRULEFreq.DAILY,
    includeByDay: false
  },
  [ImportEventRepeat.WEEKLY]: {
    freq: EventSeriesRRULEFreq.WEEKLY,
    includeByDay: true
  },
  [ImportEventRepeat.FORTNIGHTLY]: {
    freq: EventSeriesRRULEFreq.WEEKLY,
    interval: RRULE_PARTS.INTERVAL.FORTNIGHTLY_WEEKS,
    includeByDay: true
  }
};

export const IMPORT_EVENT_REPEAT_LABEL_KEYS: Record<
  Exclude<ImportEventRepeat, ImportEventRepeat.NONE>,
  string
> = {
  [ImportEventRepeat.DAILY]: 'day_plan.repeat_daily',
  [ImportEventRepeat.WEEKLY]: 'day_plan.repeat_weekly',
  [ImportEventRepeat.FORTNIGHTLY]: 'day_plan.repeat_fortnightly'
};

export const DAY_PLAN_EVENT_SOURCE_LABEL_KEYS: Partial<
  Record<DayPlanEventSource, string>
> = {
  [DayPlanEventSource.GOOGLE]: 'day_plan.platform_google',
  [DayPlanEventSource.MICROSOFT]: 'day_plan.platform_microsoft_calendar',
  [DayPlanEventSource.IMPORT]: 'day_plan.platform_file_import',
  [DayPlanEventSource.SERIES]: 'day_plan.platform_event_series'
};

export const FILE_IMPORT_STATUS_TITLE_KEYS: Record<
  FileImportStatusMode,
  string
> = {
  [FileImportStatusMode.EXTRACTING]: 'day_plan.file_reading_title',
  [FileImportStatusMode.EMPTY]: 'day_plan.review_title',
  [FileImportStatusMode.FAILED]: 'day_plan.review_title'
};

export const FILE_IMPORT_STATUS_SUBTITLE_KEYS: Partial<
  Record<FileImportStatusMode, string>
> = {
  [FileImportStatusMode.EXTRACTING]: 'day_plan.file_reading_subtitle',
  [FileImportStatusMode.EMPTY]: 'day_plan.no_events_found'
};

export const DAY_PLAN_ROUTINE_STATUS_LABEL_KEYS: Partial<
  Record<DERIVED_ROUTINE_STATUS, string>
> = {
  [DERIVED_ROUTINE_STATUS.STARTED]: 'day_plan.routine_started',
  [DERIVED_ROUTINE_STATUS.COMPLETED]: 'day_plan.routine_completed'
};

export const DAY_PLAN_ROUTINE_FORCE_COMPLETED_LABEL_KEY =
  'day_plan.routine_force_completed';

export const DAY_PLAN_HABIT_OUTCOME_LABEL_KEYS: Partial<
  Record<HABIT_COMPLETION_STATUS, string>
> = {
  [HABIT_COMPLETION_STATUS.SKIPPED]: 'day_plan.habit_skipped',
  [HABIT_COMPLETION_STATUS.PENDING]: 'day_plan.habit_missed'
};

export const DAY_PLAN_SCHEDULE_DRAWER_TITLE_KEYS: Record<
  DayPlanScheduleDrawerMode,
  string
> = {
  [DayPlanScheduleDrawerMode.ADD]: 'day_plan.schedule_task',
  [DayPlanScheduleDrawerMode.EDIT]: 'day_plan.edit_schedule'
};

export const DAY_PLAN_SCHEDULE_DRAWER_TITLE_WITH_NAME_KEYS: Record<
  DayPlanScheduleDrawerMode,
  string
> = {
  [DayPlanScheduleDrawerMode.ADD]: 'day_plan.schedule_task_with_name',
  [DayPlanScheduleDrawerMode.EDIT]: 'day_plan.edit_schedule_with_name'
};

export const DAY_PLAN_DURATION_LABEL_KEYS: Record<DurationDisplayKind, string> =
  {
    [DurationDisplayKind.SECONDS]: 'day_plan.duration_seconds',
    [DurationDisplayKind.MINUTES]: 'day_plan.duration_minutes',
    [DurationDisplayKind.HOURS]: 'day_plan.duration_hours',
    [DurationDisplayKind.HOURS_MINUTES]: 'day_plan.duration_hours_minutes'
  };

/**
 * Deep-work gap blocks shorter than this are dropped — tiny slivers between
 * near-adjacent items aren't worth surfacing (matches the native app).
 */
export const DEEP_WORK_MIN_BLOCK_MINUTES = 15;

/**
 * A deep-work block ends this many minutes before the next anchored item, giving
 * the user buffer time to wrap up and transition (matches the native app).
 */
export const DEEP_WORK_END_BUFFER_MINUTES = 2;

/** Half-width of the Day Plan date strip around today (3 past + today + 3 future). */
export const DAY_PLAN_DATE_STRIP_RADIUS = 3;

/** Total days shown in the Day Plan date strip (radius on each side + today). */
export const DAY_PLAN_DATE_STRIP_LENGTH = DAY_PLAN_DATE_STRIP_RADIUS * 2 + 1;

/** Max title chips shown on an overlapping-events cluster header. */
export const TIMELINE_EVENT_CLUSTER_MAX_TITLE_CHIPS = 4;

/** Number of days in an ISO week (Mon–Sun helpers). */
export const DAY_PLAN_WEEK_LENGTH = 7;

/** Day offsets for `DayNavDirection` NEXT / PREV in `shiftDay`. */
export const DAY_NAV_DELTA = {
  NEXT: 1,
  PREV: -1
};

/** Day Plan calendar-file allowlist (images + PDF/DOCX). Excludes GIF. */
export const DAY_PLAN_IMPORT_ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
] as const;

export type DayPlanImportAllowedMimeType =
  (typeof DAY_PLAN_IMPORT_ALLOWED_MIME_TYPES)[number];

export const DAY_PLAN_IMPORT_ACCEPT_ATTRIBUTE = [
  ...DAY_PLAN_IMPORT_ALLOWED_MIME_TYPES,
  '.pdf',
  '.docx'
].join(',');

/** Max upload size for day-plan calendar file import (10 MB). */
export const DAY_PLAN_IMPORT_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

/** Extract requests can take up to 60s for large/complex documents. */
export const DAY_PLAN_IMPORT_EXTRACT_TIMEOUT_MS = 60_000;

/** i18n key when file extract (`POST /day-plan/import/file`) fails. */
export const DAY_PLAN_IMPORT_FAILED_KEY = 'day_plan.file_import_failed';

/**
 * 8-digit hex alpha suffixes for tinted timeline row colours.
 * Appended to a `#RRGGBB` colour (e.g. `#ff880026`).
 */
export const EVENT_COLOR_HEX_ALPHA = {
  /** ~15% opacity for timeline row backgrounds. */
  BACKGROUND_TINT: '26'
} as const;

/**
 * `color-mix` weights for “Open in calendar” links on tinted event rows.
 * Event accent is blended toward near-black / near-white for contrast.
 */
export const EVENT_ACCENT_LINK_COLOR_MIX = {
  EVENT_PERCENT: 65,
  CONTRAST_PERCENT: 35,
  LIGHT_CONTRAST: '#1c1917',
  DARK_CONTRAST: '#f5f5f4'
} as const;

export const SKELETON_BLOCK_COUNT = {
  TIMELINE: 8,
  PLANNED: 5,
  EVENTS: 4,
  MANAGE_SERIES: 4
};

export const OTHER_TASKS_GROUP = {
  OTHER_TASKS_GROUP_KEY: 'other-tasks',
  OTHER_TASKS_GROUP_TEST_ID: 'other'
} as const;

export const DAY_PLAN_EMPTY_PLANNED_COPY_KEYS = {
  editable: {
    title: 'day_plan.nothing_planned_title',
    description: 'day_plan.nothing_planned_description'
  },
  past: {
    title: 'day_plan.nothing_planned_past_title',
    description: 'day_plan.nothing_planned_past_description'
  }
} as const;

/** Empty-state copy when every planned task is timeboxed onto the timeline. */
export const DAY_PLAN_ALL_TIMEBOXED_COPY_KEYS = {
  editable: {
    title: 'day_plan.all_tasks_timeboxed_title',
    description: 'day_plan.all_tasks_timeboxed_description'
  },
  past: {
    title: 'day_plan.all_tasks_timeboxed_past_title',
    description: 'day_plan.all_tasks_timeboxed_past_description'
  }
} as const;
